import { Button, Input } from 'components/atoms';
import { FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiBroom, PiIdentificationCard, PiScan } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetCountries } from 'services/enums';
import { ILabelResidenceCertificateMutation } from 'services/labels/types';
import './PopupLabelResidanceCertificateForm.scss';
import { useFormikContext, FormikHelpers } from 'formik';
import i18next from 'i18next';
import { object, string, mixed } from 'yup';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import CountrySelectPopupForm from '../country-select/CountrySelectPopupForm';
import { getCountries } from 'utils/global/countries.utils';

interface IPopupLabelResidanceCertificateFormProps {
  trigger?: JSX.Element;
  onAdd?: (certificate: ILabelResidenceCertificateMutation, actions: PopupActions) => void;
}

interface ILabelResidenceCertificateFormValues {
  ResidenceCertificateCountryCodeAlfa2: string;
  ResidenceCertificateExpirationDate: string;
  ResidenceCertificateFile: File | null;
}

const newCertificateInitialValue: ILabelResidenceCertificateFormValues = {
  ResidenceCertificateCountryCodeAlfa2: '',
  ResidenceCertificateExpirationDate: '',
  ResidenceCertificateFile: null,
};

const newCertificateValidationSchema = object().shape({
  ResidenceCertificateCountryCodeAlfa2: string().required(i18next.t('formik-error.required')),
  ResidenceCertificateExpirationDate: string().required(i18next.t('formik-error.required')),
  ResidenceCertificateFile: mixed().required(i18next.t('formik-error.required')),
});

const PopupLabelResidanceCertificateForm = ({
  trigger,
  onAdd,
}: IPopupLabelResidanceCertificateFormProps) => {
  const { t } = useTranslation();
  const countries = useGetCountries();

  const countrySelectOptions =
    countries.data?.map((country) => ({
      label: t(`countries.${country.codeAlfa2}`),
      value: country.codeAlfa2,
    })) ?? [];

  const FormContent = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    const { values, setFieldValue, resetForm, errors, touched, submitCount } =
      useFormikContext<ILabelResidenceCertificateFormValues>();

    const fileName = values.ResidenceCertificateFile?.name?.split('.')[0];
    const fileExtension = values.ResidenceCertificateFile?.name?.split('.')[1];

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (event.currentTarget.files?.[0]) {
        setFieldValue('ResidenceCertificateFile', event.currentTarget.files[0]);
        event.currentTarget.value = '';
      }
    };

    const onAddFileClick = () => inputRef.current?.click();

    const disabledClearValues =
      !values.ResidenceCertificateFile &&
      !values.ResidenceCertificateCountryCodeAlfa2 &&
      !values.ResidenceCertificateExpirationDate;

    const clearPopupValues = () => resetForm();

    return (
      <>
        {/* <FormInput.Select
          name="ResidenceCertificateCountryCodeAlfa2"
          label={t('input.label.country')}
          options={countrySelectOptions}
          displayMode={false}
          autoFocus
          appBackground
        /> */}
        <CountrySelectPopupForm
          onSelectCountry={(country, actions) => {
            setFieldValue('ResidenceCertificateCountryCodeAlfa2', country.codeAlfa2);
            actions.close();
          }}
          trigger={
            <div className="flex w-full cursor-pointer">
              <Input.Text
                required
                appBackground={true}
                name=""
                error={submitCount > 0 ? errors.ResidenceCertificateCountryCodeAlfa2 : undefined}
                label={t('input.label.country')}
                value={
                  getCountries(countries.data)?.find(
                    (c) => c.value === values.ResidenceCertificateCountryCodeAlfa2,
                  )?.label ?? ''
                }
                style={{ pointerEvents: 'none' }}
              />
            </div>
          }
        />

        <FormInput.Text
          type="date"
          label={t('input.label.expirationDate')}
          name="ResidenceCertificateExpirationDate"
          displayMode={false}
          appBackground
        />

        <Button
          variant={
            touched.ResidenceCertificateFile && errors.ResidenceCertificateFile
              ? BUTTON_VARIANT.remove
              : BUTTON_VARIANT.default
          }
          type="button"
          fullWidth
          onClick={onAddFileClick}
        >
          <Button.Icon icon={<PiScan />} />
          <Button.Text text={t('button.add-scan')} />
        </Button>

        <input ref={inputRef} className="hidden" type="file" onChange={onInputChange} />

        {touched.ResidenceCertificateFile && errors.ResidenceCertificateFile && (
          <p className="input-error">{errors.ResidenceCertificateFile}</p>
        )}

        {values.ResidenceCertificateFile && (
          <div className="file-preview-wrapper">
            <p className="file-preview-title">{t('label.certificates.popup.file')}</p>
            <div className="file-preview">
              <p className="file-preview-name" title={fileName || ''}>
                {fileName}
              </p>
              <p
                className="file-preview-extension"
                title={fileExtension ? `.${fileExtension}` : ''}
              >
                {fileExtension ? `.${fileExtension}` : ''}
              </p>
            </div>
          </div>
        )}
      </>
    );
  };

  const onSubmit = async (
    values: ILabelResidenceCertificateFormValues,
    _formikHelpers: FormikHelpers<ILabelResidenceCertificateFormValues>,
    actions: PopupActions | null,
  ) => {
    if (!onAdd || !actions || !values.ResidenceCertificateFile) return;

    const payload: ILabelResidenceCertificateMutation = {
      LabelId: '',
      ResidenceCertificateCountryCodeAlfa2: values.ResidenceCertificateCountryCodeAlfa2,
      ResidenceCertificateExpirationDate: values.ResidenceCertificateExpirationDate,
      ResidenceCertificateFile: values.ResidenceCertificateFile,
    };

    await onAdd(payload, actions);
  };

  return (
    <PopupForm<ILabelResidenceCertificateFormValues>
      initialValues={newCertificateInitialValue}
      onSubmit={onSubmit}
      validationSchema={newCertificateValidationSchema}
      submitButtonText={t('button.add')}
      header={
        <>
          <PiIdentificationCard /> {t('label.certificates.popup.header')}
        </>
      }
      trigger={trigger}
    >
      <FormContent />
    </PopupForm>
  );
};

export default PopupLabelResidanceCertificateForm;
